import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { ASSET_CATALOG } from '../assets/catalog';
import { listOverrides, type OverrideRecord } from './api';

// Sprite Animator: a port of the standalone D:\Projects\sprite-animator tool into
// the studio. Mirrors VERSUS render/utils/spriteSheet.ts (computeUnionTrim) so a
// designer sees a sheet anchored EXACTLY as the game would (anchor 0.5/1, cell
// centre X, robust ground line). Two sheets can be loaded from disk or picked
// from the system catalog, tuned (offsetX/Y/scale), and overlaid for comparison.

const CONTENT_ALPHA_THRESHOLD = 10;
const GROUND_MIN_COVERAGE = 0.05;
const CANVAS_PADDING = 14;
const CANVAS_W = 384;
const CANVAS_H = 300;
const DEFAULT_FPS = 8;
const DEFAULT_COLS = 4;
const DEFAULT_ROWS = 4;

type Trim = {
  x: number;
  y: number;
  w: number;
  h: number;
  cellW: number;
  cellH: number;
  minSolid: number;
  fallback: boolean;
};

type Sprite = {
  img: HTMLImageElement;
  trim: Trim;
  cols: number;
  rows: number;
  offX: number;
  offY: number;
  scaleMul: number;
};

type AnimState = {
  fps: number;
  playing: boolean;
  frame: number;
  lastTick: number;
  anchorOffsetX: number;
  compare: boolean;
  spriteA: Sprite | null;
  spriteB: Sprite | null;
};

type PanelUi = {
  name: string;
  error: string;
  stats: string;
  offX: number;
  offY: number;
  scale: number;
  cols: number;
  rows: number;
  loaded: boolean;
};

type Slot = 'A' | 'B';

const emptyPanel = (): PanelUi => ({
  name: '',
  error: '',
  stats: '',
  offX: 0,
  offY: 0,
  scale: 1,
  cols: DEFAULT_COLS,
  rows: DEFAULT_ROWS,
  loaded: false,
});

// Loads an image element. Tries crossOrigin=anonymous first so getImageData on a
// remote (Storage) sheet isn't tainted; falls back to a plain load if CORS isn't
// allowed (display still works, trim degrades to the raw cell).
function loadImage(src: string): Promise<HTMLImageElement> {
  const remote = /^https?:\/\//i.test(src);
  return new Promise((resolve, reject) => {
    const attempt = (useCors: boolean): void => {
      const img = new Image();
      if (useCors) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => (useCors ? attempt(false) : reject(new Error('load failed')));
      img.src = src;
    };
    attempt(remote);
  });
}

// Port of VERSUS computeUnionTrim. One trim rect for the whole sheet: VERTICAL is
// content-based (top content row .. robust ground line across the union of all
// frames), HORIZONTAL stays full cell width (cell-centre anchor). On a tainted
// canvas or no solid row it returns a raw-cell fallback so the sheet still plays.
function computeUnionTrim(img: HTMLImageElement, cols: number, rows: number): Trim {
  const W = img.width;
  const H = img.height;
  const cellW = W / cols;
  const cellH = H / rows;
  const cellWPx = Math.floor(cellW);
  const cellHPx = Math.floor(cellH);

  const rawCell = (): Trim => ({
    x: 0,
    y: 0,
    w: cellWPx,
    h: cellHPx,
    cellW,
    cellH,
    minSolid: Math.max(3, Math.round(cellWPx * GROUND_MIN_COVERAGE)),
    fallback: true,
  });

  const off = document.createElement('canvas');
  off.width = W;
  off.height = H;
  const ctx = off.getContext('2d', { willReadFrequently: true });
  if (!ctx) return rawCell();
  let data: Uint8ClampedArray;
  try {
    ctx.drawImage(img, 0, 0);
    data = ctx.getImageData(0, 0, W, H).data;
  } catch {
    return rawCell();
  }

  const minSolid = Math.max(3, Math.round(cellWPx * GROUND_MIN_COVERAGE));
  let minY = -1;
  let feetY = -1;
  for (let y = 0; y < cellHPx; y++) {
    let count = 0;
    for (let x = 0; x < cellWPx; x++) {
      let opaque = false;
      for (let r = 0; r < rows && !opaque; r++) {
        for (let c = 0; c < cols; c++) {
          const sx = Math.floor(c * cellW) + x;
          const sy = Math.floor(r * cellH) + y;
          if ((data[(sy * W + sx) * 4 + 3] ?? 0) > CONTENT_ALPHA_THRESHOLD) {
            opaque = true;
            break;
          }
        }
      }
      if (opaque) count++;
    }
    if (count === 0) continue;
    if (minY < 0) minY = y;
    if (count >= minSolid) feetY = y;
  }
  if (minY < 0 || feetY < 0) return rawCell();
  return { x: 0, y: minY, w: cellWPx, h: feetY - minY + 1, cellW, cellH, minSolid, fallback: false };
}

function makeSprite(img: HTMLImageElement, cols: number, rows: number): Sprite {
  return { img, trim: computeUnionTrim(img, cols, rows), cols, rows, offX: 0, offY: 0, scaleMul: 1 };
}

function totalFrames(sprite: Sprite | null): number {
  return sprite ? sprite.cols * sprite.rows : DEFAULT_COLS * DEFAULT_ROWS;
}

// Draws one frame exactly like the game: full-cell-width sub-rect, bottom pinned
// to a fixed ground line, scale = targetH / trimmedHeight, then anchorOffsetX
// (shared) + per-sheet offX/offY/scaleMul.
function drawFrame(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  frameIndex: number,
  anchorOffsetX: number,
): void {
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  const { img, trim, cols } = sprite;
  const total = sprite.cols * sprite.rows;
  const fi = ((frameIndex % total) + total) % total;
  const col = fi % cols;
  const row = Math.floor(fi / cols);
  const srcX = col * trim.cellW + trim.x;
  const srcY = row * trim.cellH + trim.y;
  const srcW = trim.w;
  const srcH = trim.h;

  const targetH = CANVAS_H - CANVAS_PADDING * 2;
  const scale = (targetH / srcH) * sprite.scaleMul;
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const groundY = CANVAS_H - CANVAS_PADDING;
  const centerX = CANVAS_W / 2;
  const ox = (anchorOffsetX + sprite.offX) * scale;
  const oy = sprite.offY * scale;
  const dx = centerX - drawW / 2 + ox;
  const dy = groundY - drawH + oy;
  ctx.drawImage(img, srcX, srcY, srcW, srcH, dx, dy, drawW, drawH);
}

function trimReadout(sprite: Sprite | null, name: string, anchorOffsetX: number): string {
  if (!sprite) return '';
  const t = sprite.trim;
  const targetH = CANVAS_H - CANVAS_PADDING * 2;
  const scale = (targetH / t.h) * sprite.scaleMul;
  const feetY = t.y + t.h - 1;
  const vGuides: number[] = [];
  for (let c = 0; c < sprite.cols; c++) vGuides.push(Math.round((c + 0.5) * t.cellW));
  const gGuides: number[] = [];
  for (let r = 0; r < sprite.rows; r++) gGuides.push(Math.round(r * t.cellH + feetY));
  return [
    `--- ${name} (${sprite.cols}x${sprite.rows}) ---`,
    t.fallback
      ? 'trim   : FALLBACK (no solid row) -> raw cell'
      : `trim   : y=${t.y} h=${t.h} (full width ${t.w})`,
    `minSolid: ${t.minSolid}px`,
    `fit-scale: ${scale.toFixed(3)}  (targetH/${t.h} * ${sprite.scaleMul})`,
    `timings  : { offsetX: ${sprite.offX}, offsetY: ${sprite.offY}, scale: ${sprite.scaleMul} }`,
    `anchorOffsetX (shared): ${anchorOffsetX}`,
    'PS guides (View > New Guide, px):',
    `  vertical X: ${vGuides.join(', ')}`,
    `  ground   Y: ${gGuides.join(', ')}`,
  ].join('\n');
}

type SheetEntry = { key: string; label: string; group: string; url: string; cols: number; rows: number };

export function SpriteAnimatorView(): JSX.Element {
  const canvasA = useRef<HTMLCanvasElement>(null);
  const canvasB = useRef<HTMLCanvasElement>(null);
  const canvasC1 = useRef<HTMLCanvasElement>(null);
  const canvasC2 = useRef<HTMLCanvasElement>(null);

  const stateRef = useRef<AnimState>({
    fps: DEFAULT_FPS,
    playing: true,
    frame: 0,
    lastTick: 0,
    anchorOffsetX: 0,
    compare: false,
    spriteA: null,
    spriteB: null,
  });

  const [playing, setPlaying] = useState(true);
  const [fps, setFps] = useState(DEFAULT_FPS);
  const [frame, setFrame] = useState(0);
  const [anchorOffsetX, setAnchorOffsetX] = useState(0);
  const [compare, setCompare] = useState(false);
  const [bg, setBg] = useState<'checker' | 'black' | 'white'>('checker');
  const [overlayAlpha, setOverlayAlpha] = useState(50);
  const [blend, setBlend] = useState('normal');
  const [panelA, setPanelA] = useState<PanelUi>(emptyPanel);
  const [panelB, setPanelB] = useState<PanelUi>(emptyPanel);
  const [picker, setPicker] = useState<Slot | null>(null);
  const [overrides, setOverrides] = useState<Map<string, OverrideRecord>>(new Map());

  useEffect(() => {
    listOverrides()
      .then(setOverrides)
      .catch(() => {});
  }, []);

  // Sheet-kind catalog entries, resolved to their current (overridden) url/grid.
  const sheetEntries = useMemo<SheetEntry[]>(
    () =>
      ASSET_CATALOG.filter((e) => e.kind === 'sheet').map((e) => {
        const ov = overrides.get(e.key);
        return {
          key: e.key,
          label: e.label,
          group: e.group,
          url: ov?.url ?? e.key,
          cols: ov?.cols ?? e.cols ?? DEFAULT_COLS,
          rows: ov?.rows ?? e.rows ?? DEFAULT_ROWS,
        };
      }),
    [overrides],
  );

  const setPanel = useCallback(
    (slot: Slot, patch: Partial<PanelUi>): void =>
      (slot === 'A' ? setPanelA : setPanelB)((p) => ({ ...p, ...patch })),
    [],
  );

  const refreshStats = useCallback((): void => {
    const s = stateRef.current;
    setPanelA((p) => ({ ...p, stats: trimReadout(s.spriteA, 'A', s.anchorOffsetX) }));
    setPanelB((p) => ({ ...p, stats: trimReadout(s.spriteB, 'B', s.anchorOffsetX) }));
  }, []);

  // Animation loop: imperative on the canvas refs, reading the mutable stateRef
  // so it never goes stale on a control change.
  useEffect(() => {
    let raf = 0;
    const tick = (now: number): void => {
      const s = stateRef.current;
      if (s.playing && (s.spriteA || s.spriteB)) {
        const interval = 1000 / s.fps;
        if (now - s.lastTick >= interval) {
          const total = totalFrames(s.spriteA ?? s.spriteB);
          s.frame = (s.frame + 1) % total;
          s.lastTick = now;
          setFrame(s.frame);
        }
      }
      const ctxA = canvasA.current?.getContext('2d');
      const ctxB = canvasB.current?.getContext('2d');
      if (ctxA && s.spriteA) drawFrame(ctxA, s.spriteA, s.frame, s.anchorOffsetX);
      if (ctxB && s.spriteB) drawFrame(ctxB, s.spriteB, s.frame, s.anchorOffsetX);
      if (s.compare) {
        const c1 = canvasC1.current?.getContext('2d');
        const c2 = canvasC2.current?.getContext('2d');
        if (c1) {
          if (s.spriteA) drawFrame(c1, s.spriteA, s.frame, s.anchorOffsetX);
          else c1.clearRect(0, 0, CANVAS_W, CANVAS_H);
        }
        if (c2) {
          if (s.spriteB) drawFrame(c2, s.spriteB, s.frame, s.anchorOffsetX);
          else c2.clearRect(0, 0, CANVAS_W, CANVAS_H);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const applySprite = useCallback(
    (slot: Slot, sprite: Sprite, name: string): void => {
      const s = stateRef.current;
      if (slot === 'A') s.spriteA = sprite;
      else s.spriteB = sprite;
      setPanel(slot, {
        name,
        error: '',
        loaded: true,
        offX: 0,
        offY: 0,
        scale: 1,
        cols: sprite.cols,
        rows: sprite.rows,
      });
      refreshStats();
    },
    [refreshStats, setPanel],
  );

  // Default both slots to the character idle sheet so the tab isn't empty on
  // open. Runs once; matched by label (survives an idle_vN.png rename).
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    const idle = sheetEntries.find((e) => e.label.toLowerCase().startsWith('idle'));
    if (!idle) return;
    didInit.current = true;
    loadImage(idle.url)
      .then((img) => {
        applySprite('A', makeSprite(img, idle.cols, idle.rows), idle.label);
        applySprite('B', makeSprite(img, idle.cols, idle.rows), idle.label);
      })
      .catch(() => {
        didInit.current = false;
      });
  }, [sheetEntries, applySprite]);

  const onUpload = useCallback(
    async (slot: Slot, file: File | null): Promise<void> => {
      if (!file) return;
      const ui = slot === 'A' ? panelA : panelB;
      try {
        const img = await loadImage(URL.createObjectURL(file));
        applySprite(slot, makeSprite(img, ui.cols, ui.rows), file.name);
      } catch {
        setPanel(slot, { error: 'Could not load image' });
      }
    },
    [applySprite, panelA, panelB, setPanel],
  );

  const onPickSystem = useCallback(
    async (entry: SheetEntry): Promise<void> => {
      const slot = picker;
      setPicker(null);
      if (!slot) return;
      try {
        const img = await loadImage(entry.url);
        applySprite(slot, makeSprite(img, entry.cols, entry.rows), entry.label);
      } catch {
        setPanel(slot, { error: 'Could not load image' });
      }
    },
    [picker, applySprite, setPanel],
  );

  // Re-grid a loaded sheet: trim depends on the grid, so recompute it (keeping the
  // current tune values).
  const onGridChange = useCallback(
    (slot: Slot, cols: number, rows: number): void => {
      setPanel(slot, { cols, rows });
      const s = stateRef.current;
      const sprite = slot === 'A' ? s.spriteA : s.spriteB;
      if (!sprite || cols < 1 || rows < 1) return;
      const next = makeSprite(sprite.img, cols, rows);
      next.offX = sprite.offX;
      next.offY = sprite.offY;
      next.scaleMul = sprite.scaleMul;
      if (slot === 'A') s.spriteA = next;
      else s.spriteB = next;
      refreshStats();
    },
    [refreshStats, setPanel],
  );

  const onTune = useCallback(
    (slot: Slot, field: 'offX' | 'offY' | 'scale', value: number): void => {
      setPanel(slot, { [field]: value });
      const s = stateRef.current;
      const sprite = slot === 'A' ? s.spriteA : s.spriteB;
      if (!sprite) return;
      if (field === 'offX') sprite.offX = value || 0;
      else if (field === 'offY') sprite.offY = value || 0;
      else sprite.scaleMul = value || 1;
      refreshStats();
    },
    [refreshStats, setPanel],
  );

  const togglePlay = useCallback((): void => {
    const next = !stateRef.current.playing;
    stateRef.current.playing = next;
    if (next) stateRef.current.lastTick = performance.now();
    setPlaying(next);
  }, []);

  const stepFrame = useCallback((delta: number): void => {
    const s = stateRef.current;
    s.playing = false;
    setPlaying(false);
    const total = totalFrames(s.spriteA ?? s.spriteB);
    s.frame = (s.frame + delta + total) % total;
    setFrame(s.frame);
  }, []);

  const onFps = useCallback((v: number): void => {
    stateRef.current.fps = v;
    setFps(v);
  }, []);

  const onAnchor = useCallback(
    (v: number): void => {
      stateRef.current.anchorOffsetX = v;
      setAnchorOffsetX(v);
      refreshStats();
    },
    [refreshStats],
  );

  const onToggleCompare = useCallback((): void => {
    const next = !stateRef.current.compare;
    stateRef.current.compare = next;
    setCompare(next);
  }, []);

  const reset = useCallback((): void => {
    const s = stateRef.current;
    s.fps = DEFAULT_FPS;
    s.anchorOffsetX = 0;
    setFps(DEFAULT_FPS);
    setAnchorOffsetX(0);
    for (const sprite of [s.spriteA, s.spriteB]) {
      if (sprite) {
        sprite.offX = 0;
        sprite.offY = 0;
        sprite.scaleMul = 1;
      }
    }
    setPanelA((p) => ({ ...p, offX: 0, offY: 0, scale: 1 }));
    setPanelB((p) => ({ ...p, offX: 0, offY: 0, scale: 1 }));
    refreshStats();
  }, [refreshStats]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
      if (e.key === 'ArrowLeft') stepFrame(-1);
      else if (e.key === 'ArrowRight') stepFrame(1);
      else if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepFrame, togglePlay]);

  const total = totalFrames(stateRef.current.spriteA ?? stateRef.current.spriteB);

  return (
    <div className="sprite-animator" data-bg={bg}>
      <div className="sa-stage">
        <SpritePanel
          slot="A"
          title="Sprite A"
          ui={panelA}
          canvasRef={canvasA}
          onUpload={onUpload}
          onPick={() => setPicker('A')}
          onGridChange={onGridChange}
          onTune={onTune}
        />
        <SpritePanel
          slot="B"
          title="Sprite B"
          ui={panelB}
          canvasRef={canvasB}
          onUpload={onUpload}
          onPick={() => setPicker('B')}
          onGridChange={onGridChange}
          onTune={onTune}
        />

        {compare && (
          <div className="sa-panel">
            <div className="sa-panel-title">Overlay (both aligned)</div>
            <div className="sa-canvas-wrap sa-overlay-wrap">
              <canvas ref={canvasC1} width={CANVAS_W} height={CANVAS_H} />
              <canvas
                ref={canvasC2}
                width={CANVAS_W}
                height={CANVAS_H}
                className="sa-overlay-top"
                style={{
                  opacity: overlayAlpha / 100,
                  mixBlendMode: blend as React.CSSProperties['mixBlendMode'],
                }}
              />
            </div>
            <div className="sa-overlay-controls">
              <label>Opacity</label>
              <input
                type="range"
                min={0}
                max={100}
                value={overlayAlpha}
                onChange={(e) => setOverlayAlpha(Number(e.target.value))}
              />
              <span className="sa-value">{overlayAlpha}%</span>
            </div>
            <div className="sa-overlay-controls">
              <label>Blend</label>
              <select value={blend} onChange={(e) => setBlend(e.target.value)}>
                <option value="normal">Normal</option>
                <option value="difference">Difference</option>
                <option value="multiply">Multiply</option>
                <option value="screen">Screen</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="sa-controls">
        <button className="sa-btn" style={{ minWidth: 72 }} onClick={togglePlay}>
          {playing ? 'Pause' : 'Play'}
        </button>
        <div className="sa-control">
          <button className="sa-btn secondary" title="Previous frame" onClick={() => stepFrame(-1)}>
            &lt;
          </button>
          <span className="sa-value">
            {frame + 1} / {total}
          </span>
          <button className="sa-btn secondary" title="Next frame" onClick={() => stepFrame(1)}>
            &gt;
          </button>
        </div>
        <div className="sa-control">
          <label>Speed</label>
          <input
            type="range"
            min={1}
            max={30}
            value={fps}
            onChange={(e) => onFps(Number(e.target.value))}
          />
          <span className="sa-value">{fps} fps</span>
        </div>
        <div className="sa-control">
          <label>anchorOffsetX</label>
          <input
            type="range"
            min={-40}
            max={40}
            step={0.5}
            value={anchorOffsetX}
            onChange={(e) => onAnchor(Number(e.target.value))}
          />
          <span className="sa-value">{anchorOffsetX}</span>
        </div>
        <div className="sa-control">
          <label>Background</label>
          <select value={bg} onChange={(e) => setBg(e.target.value as typeof bg)}>
            <option value="checker">Checker</option>
            <option value="black">Black</option>
            <option value="white">White</option>
          </select>
        </div>
        <button className="sa-btn secondary" onClick={reset}>
          Reset
        </button>
        <button className={`sa-btn ${compare ? 'active' : ''}`} onClick={onToggleCompare}>
          {compare ? 'Hide compare' : 'Compare'}
        </button>
      </div>

      {picker && (
        <SheetPickerModal
          entries={sheetEntries}
          onPick={onPickSystem}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

function SpritePanel(props: {
  slot: Slot;
  title: string;
  ui: PanelUi;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onUpload: (slot: Slot, file: File | null) => void;
  onPick: () => void;
  onGridChange: (slot: Slot, cols: number, rows: number) => void;
  onTune: (slot: Slot, field: 'offX' | 'offY' | 'scale', value: number) => void;
}): JSX.Element {
  const { slot, title, ui, canvasRef, onUpload, onPick, onGridChange, onTune } = props;
  return (
    <div className="sa-panel">
      <div className="sa-panel-title">{title}</div>
      <div className="sa-canvas-wrap">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          style={{ display: ui.loaded ? 'block' : 'none' }}
        />
        {ui.loaded && <div className="sa-center-guide" />}
        {ui.loaded && <div className="sa-ground-guide" />}
        {!ui.loaded && (
          <div className="sa-placeholder">
            Upload a sprite sheet (PNG with alpha)
            <br />
            or pick one from the system
          </div>
        )}
      </div>
      <div className="sa-upload-row">
        <button className="sa-btn" onClick={onPick}>
          From system
        </button>
        <label className="sa-btn secondary">
          Choose file
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => onUpload(slot, e.target.files?.[0] ?? null)}
          />
        </label>
        <span className="sa-file-name">{ui.name}</span>
      </div>
      <div className="sa-tune-row">
        <span className="sa-tune">
          cols
          <input
            type="number"
            min={1}
            value={ui.cols}
            onChange={(e) => onGridChange(slot, Number(e.target.value) || 1, ui.rows)}
          />
        </span>
        <span className="sa-tune">
          rows
          <input
            type="number"
            min={1}
            value={ui.rows}
            onChange={(e) => onGridChange(slot, ui.cols, Number(e.target.value) || 1)}
          />
        </span>
      </div>
      <div className="sa-tune-row">
        <span className="sa-tune">
          offX
          <input
            type="number"
            step={0.5}
            value={ui.offX}
            onChange={(e) => onTune(slot, 'offX', Number(e.target.value))}
          />
        </span>
        <span className="sa-tune">
          offY
          <input
            type="number"
            step={0.5}
            value={ui.offY}
            onChange={(e) => onTune(slot, 'offY', Number(e.target.value))}
          />
        </span>
        <span className="sa-tune">
          scale
          <input
            type="number"
            step={0.005}
            value={ui.scale}
            onChange={(e) => onTune(slot, 'scale', Number(e.target.value))}
          />
        </span>
      </div>
      {ui.error && <div className="sa-error">{ui.error}</div>}
      {ui.stats && <div className="sa-stats">{ui.stats}</div>}
    </div>
  );
}

// Single-select picker over the system's sheet animations, grouped by catalog
// group. Thumbnails are the sheets themselves.
function SheetPickerModal(props: {
  entries: SheetEntry[];
  onPick: (entry: SheetEntry) => void;
  onClose: () => void;
}): JSX.Element {
  const { entries, onPick, onClose } = props;
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const items = entries.filter(
    (e) => q === '' || e.label.toLowerCase().includes(q) || e.key.toLowerCase().includes(q),
  );

  return (
    <RemoveScroll>
      <div className="picker-overlay" onClick={onClose}>
        <div className="picker-panel" onClick={(e) => e.stopPropagation()}>
          <div className="picker-header">
            <strong>Choose animation</strong>
            <div className="picker-search-wrap">
              <i className="fas fa-search" />
              <input
                className="picker-search"
                placeholder="Search by name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <button className="modal-close" onClick={onClose} title="Close">
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="picker-grid">
            {items.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className="picker-card"
                onClick={() => onPick(entry)}
                title={`${entry.key} (${entry.cols}x${entry.rows})`}
              >
                <img className="picker-thumb" src={entry.url} alt="" loading="lazy" />
                <span className="picker-name">{entry.label}</span>
              </button>
            ))}
            {items.length === 0 && <div className="muted">No matches.</div>}
          </div>
        </div>
      </div>
    </RemoveScroll>
  );
}
